import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Button, Row, Col, Card, Image, Embed } from "react-bootstrap";
import styled from "styled-components";

// Styled components
const ProgramContainer = styled(Container)`
  padding: 5rem 0;
  min-height: 80vh;
`;

const ProgramHeader = styled.h2`
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ProgramDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #34495e;
  margin-bottom: 2rem;
`;

const BackButton = styled(Button)`
  background-color: #3498db;
  border: none;
  padding: 0.5rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: #2980b9;
    transform: translateY(-2px);
  }
`;

const ProgramImage = styled(Image)`
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const VideoContainer = styled.div`
  margin: 2rem 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const trainingPrograms = [
  {
    name: "AutoCAD",
    description: "Comprehensive AutoCAD training covering 2D drafting and design. Our expert-led course takes you from beginner to advanced level, teaching you how to create precise technical drawings, work with layers and blocks, and automate tasks with AutoLISP. Perfect for architects, engineers, and designers looking to enhance their drafting skills.",
    image: "/2d.jpg",
    videoUrl: "https://www.youtube.com/embed/ItGVa6QhLQM" // Example video ID
  },
  {
    name: "2D Drafting",
    description: "Learn precise 2D drafting techniques for engineering projects. This course focuses on creating accurate technical drawings, understanding industry standards, and using advanced tools for dimensioning and annotation. You'll gain hands-on experience with real-world projects that will prepare you for professional drafting work.",
    image: "/programs/2d-drafting.jpg",
    videoUrl: "https://www.youtube.com/embed/ItGVa6QhLQM" // Example video ID
  },
  {
    name: "3D Modeling",
    description: "Master 3D modeling skills using industry-standard software. Our comprehensive training covers solid modeling, surface modeling, rendering techniques, and animation. You'll learn to create complex 3D designs for product development, architectural visualization, and engineering simulations.",
    image: "/programs/3d-modeling.jpg",
    videoUrl: "https://www.youtube.com/embed/ItGVa6QhLQM" // Example video ID
  },
  {
    name: "Revit",
    description: "In-depth Revit training for building information modeling (BIM). This course teaches you how to create intelligent 3D models of buildings, generate construction documentation, and collaborate with other professionals in the architecture, engineering, and construction industries.",
    image: "/programs/revit.jpg",
    videoUrl: "https://www.youtube.com/embed/ItGVa6QhLQM" // Example video ID
  },
  {
    name: "Inventor",
    description: "Professional training in Autodesk Inventor for 3D mechanical design. Learn parametric modeling, assembly design, simulation, and documentation for manufacturing. Perfect for mechanical engineers and product designers looking to bring their ideas to life with professional 3D CAD tools.",
    image: "/programs/inventor.jpg",
    videoUrl: "https://www.youtube.com/embed/ItGVa6QhLQM" // Example video ID
  },
];

const TrainingProgramDetail = () => {
  const { programName } = useParams();
  const navigate = useNavigate();

  const program = trainingPrograms.find(
    (p) => p.name.toLowerCase() === programName?.toLowerCase()
  );

  if (!program) {
    return (
      <ProgramContainer className="text-center">
        <Card className="p-4 shadow">
          <h2 className="text-danger">Program Not Found</h2>
          <p className="mb-4">The requested training program could not be found.</p>
          <BackButton onClick={() => navigate("/training-and-development")}>
            Back to Training & Development
          </BackButton>
        </Card>
      </ProgramContainer>
    );
  }

  return (
    <ProgramContainer>
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="border-0 shadow-sm p-4">
            <ProgramHeader>{program.name}</ProgramHeader>
            
            <Row className="align-items-center mb-4">
              <Col md={6} className="mb-4 mb-md-0">
                <ProgramImage 
                  src={program.image} 
                  alt={program.name} 
                  fluid 
                  thumbnail 
                />
              </Col>
              <Col md={6}>
                <ProgramDescription>{program.description}</ProgramDescription>
              </Col>
            </Row>
            
            {program.videoUrl && (
              <Row>
                <Col>
                  <h4 className="mb-3">Training Video</h4>
                  <VideoContainer>
                    <div className="ratio ratio-16x9">
                      <iframe 
                        src={program.videoUrl}
                        title={`${program.name} Training Video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    </div>
                  </VideoContainer>
                </Col>
              </Row>
            )}
            
            <div className="text-center mt-4">
              <BackButton onClick={() => navigate("/training-and-development")}>
                Back to Training & Development
              </BackButton>
            </div>
          </Card>
        </Col>
      </Row>
    </ProgramContainer>
  );
};

export default TrainingProgramDetail;